import { FC, useContext } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button, CircularProgress, DialogActions } from '@mui/material';
import { useSelectedHeroConversation } from '../../../hooks/useSelectedHeroConversation';
import { TransferContext } from './TransferContext';
import { TransferFormFieldValues } from './SelectedConversationTransferModal';
import {
  OutgoingTransferFormErrors,
  useCreateAssociateTransferRequest,
  useGetAssociateOutgoingOpenTransferRequests,
} from '@vs-online-only-app/state';

type Props = {
  onClose: () => void;
};

const TransferModalButtons: FC<Props> = ({ onClose }) => {
  const { filteredAssociates, isLoading } = useContext(TransferContext);
  const conversation = useSelectedHeroConversation();
  const getAssociateOutgoingOpenTransferRequestsQuery =
    useGetAssociateOutgoingOpenTransferRequests();
  const createTransferMutation = useCreateAssociateTransferRequest();

  const {
    handleSubmit,
    setError,
    formState: { errors: formErrors },
  } = useFormContext<TransferFormFieldValues>();

  const onSuccess = () => {
    getAssociateOutgoingOpenTransferRequestsQuery.refetch();
    onClose();
  };

  const onClick = () => {
    if (!conversation) return;

    handleSubmit(async (formValues) => {
      try {
        await createTransferMutation.mutateAsync(
          {
            associateIds: formValues.selectedAssociateIds,
            note: formValues.note,
            conversationId: conversation.id,
          },
          {
            onSuccess,
          },
        );
      } catch (e) {
        const isOfflineError =
          (e as { error: string }).error ===
          OutgoingTransferFormErrors.ALL_ASSOCIATES_OFFLINE;

        if (isOfflineError) {
          setError('root', {
            type: OutgoingTransferFormErrors.ALL_ASSOCIATES_OFFLINE,
            message:
              formValues.selectedAssociateIds.length > 1
                ? 'All team members you attempted to transfer to recently went offline. Please select someone else.'
                : `${
                    filteredAssociates.find(
                      (associate) =>
                        associate.id === formValues.selectedAssociateIds[0],
                    )?.firstName
                  } recently went offline. Please select someone else.`,
          });
        }
      }
    })();
  };

  const disabled =
    !filteredAssociates.length || Object.keys(formErrors).length > 0;

  return (
    <DialogActions>
      <Button
        onClick={onClose}
        disabled={createTransferMutation.isLoading}
        variant="text"
        fullWidth
      >
        Cancel request
      </Button>
      <Button
        disabled={disabled}
        onClick={onClick}
        fullWidth
        variant="contained"
      >
        {isLoading ? (
          <CircularProgress color="inherit" size={20} />
        ) : (
          'Request transfer'
        )}
      </Button>
    </DialogActions>
  );
};

export default TransferModalButtons;
